/* eslint-disable max-lines */
import { useIsRibbonSidebarEnabled } from '@/app-shell/flags/agents-flags';
import { useIsHeidi2Clinician } from '@/app-shell/modals/trial-extension/use-is-heidi-2-clinician';
import { useOfflineGatedTooltip } from '@/app-shell/offline-recording/use-offline-gated-tooltip';
import { useQueryGamifyTrialExtension } from '@/hooks/query/useQueryGamifyTrialExtension';
import useReverseTrial from '@/hooks/useReverseTrial';
import { cn } from '@/lib/cn';
import { isAppleCompliantBuild } from '@/utils/appConfiguration';
import { trackButtonClicked, trackModalViewed } from '@heidi/analytics/events';
import { ButtonAction, ScreenName } from '@heidi/analytics/vocabulary';
import {
  getHoverCardCta,
  getHoverCardSubtitle,
  getTrialExtensionProgress,
  trialExtensionProgressMessages,
} from '@heidi/commerce';
import {
  earnDaysNavPermanentlyDismissedAtom,
  hasSeenTrialCompletionTooltipAtom,
  trialExtensionModalOpenAtom,
} from '@heidi/commerce/data';
import { Button } from '@heidi/design-system/button';
import {
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from '@heidi/design-system/page-shell';
import { Popover, PopoverContent, PopoverTrigger } from '@heidi/design-system/popover';
import { Progress } from '@heidi/design-system/progress';
import { Tooltip, TooltipContent, TooltipTrigger } from '@heidi/design-system/tooltip';
import {
  TypographyCaption,
  TypographyFootNote,
  TypographyP2SemiBold,
} from '@heidi/design-system/typography';
import { useTranslations } from '@heidi/runtime/i18n';
import { useAtomValue, useSetAtom } from 'jotai';
import { Gift } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { useMobilePanel } from './mobile/use-mobile-panel';
import { TrialCompletionTooltip } from './TrialCompletionTooltip';
import { useIsGamifyTrialExtensionNavEnabled } from './use-is-gamify-trial-extension-entry-enabled';

const POPOVER_HOVER_OPEN_DELAY_MS = 200;
const POPOVER_HOVER_CLOSE_DELAY_MS = 100;

const messages = defineMessages({
  tierNameClinician: {
    defaultMessage: 'Clinician',
    id: 'U9LdwIoZH5',
    description: 'Trial tier name used in the trial-extension hover card copy',
  },
  tierNameEvidence: {
    defaultMessage: 'Evidence',
    id: 'eSmRWgIkgm',
    description: 'Trial tier name used in the trial-extension hover card copy',
  },
  tierNamePractice: {
    defaultMessage: 'Practice',
    id: 'jdoWvREeL9',
    description: 'Trial tier name used in the trial-extension hover card copy',
  },
  navLabelEarnedComplete: {
    defaultMessage: '{days, plural, one {# free day} other {# free days}} added',
    id: 'I4hKQCZP9H',
    description: 'Sidebar label once every trial-extension task is complete',
  },
  hoverCardTitle: {
    defaultMessage: 'Earn {days, plural, one {# free day} other {# free days}}',
    id: 'XvjjYgu5On',
    description: 'Sidebar and hover-card title inviting the user to earn trial days',
  },
  hoverCardTitleEarnedComplete: {
    defaultMessage: '{days, plural, one {# free day} other {# free days}} earned',
    id: 'jAW5cdCLy4',
    description: 'Hover-card title once every trial-extension task is complete',
  },
  earnFreeTrialDays: {
    defaultMessage:
      'Earn {days, plural, one {# trial day} other {# trial days}} for free',
    id: '5yYF1Uczj3',
    description: 'Accessible label for the trial-extension sidebar button',
  },
  progressLabel: {
    defaultMessage:
      'Trial extension progress, {completed} of {total, plural, one {# action} other {# actions}}',
    id: 'ZhNgud2Ltk',
    description: 'Accessible label for the trial-extension progress bar',
  },
  collapsedTooltipAllComplete: {
    defaultMessage: 'All tasks complete',
    id: '8GUAdmuw9q',
    description: 'Collapsed-sidebar tooltip detail when every trial task is done',
  },
  collapsedTooltipProgress: {
    defaultMessage: '{completed}/{total, plural, one {# task} other {# tasks}}',
    id: 'K2pbym7H74',
    description: 'Collapsed-sidebar tooltip detail showing trial task progress',
  },
});

export function TrialCountdownButton() {
  const setTrialExtensionModalOpen = useSetAtom(trialExtensionModalOpenAtom);
  const setEarnDaysNavPermanentlyDismissed = useSetAtom(
    earnDaysNavPermanentlyDismissedAtom
  );
  const earnDaysNavPermanentlyDismissed = useAtomValue(
    earnDaysNavPermanentlyDismissedAtom
  );
  const hasSeenTrialCompletionTooltip = useAtomValue(hasSeenTrialCompletionTooltipAtom);
  const { isClinicianTrialUser, isFakeClinicianTrial, isEvidenceTrialUser } =
    useReverseTrial();
  // `t` is only forwarded to getHoverCardSubtitle/getHoverCardCta, which still
  // read i18next keys; it goes away when @heidi/commerce migrates those.
  const { t } = useTranslations();
  // Still needed on its own: the hover-card subtitle formats through `intl`
  // directly. The gated-tooltip copy no longer does.
  const intl = useIntl();
  const { isOfflineGated, offlineTooltip } = useOfflineGatedTooltip();
  const showButton = useIsGamifyTrialExtensionNavEnabled();

  const queryEnabled = showButton && !earnDaysNavPermanentlyDismissed;
  const { data } = useQueryGamifyTrialExtension(queryEnabled);
  const { state: sidebarState, isMobile } = useSidebar();
  const { dismissMobileSidebar } = useMobilePanel();
  const isRibbonSidebarEnabled = useIsRibbonSidebarEnabled();
  const isHeidi2Clinician = useIsHeidi2Clinician(data?.tier);

  const [popoverOpen, setPopoverOpen] = useState(false);
  const openDelayTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const closeDelayTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const hoverOpenedRef = useRef(false);
  const clickOpenedModalRef = useRef(false);

  useEffect(() => {
    return () => {
      if (openDelayTimeoutRef.current) clearTimeout(openDelayTimeoutRef.current);
      if (closeDelayTimeoutRef.current) clearTimeout(closeDelayTimeoutRef.current);
    };
  }, []);

  if (!showButton || isAppleCompliantBuild()) {
    return null;
  }

  if (earnDaysNavPermanentlyDismissed) {
    return null;
  }

  const progress = getTrialExtensionProgress(data);
  const {
    completedCount,
    totalCount,
    progressPercent,
    allTasksComplete,
    hasStartedActions,
  } = progress;
  const trialDays = data?.trial_extension_days ?? 14;
  const hoverCardCopyOptions = { isHeidi2Clinician, days: trialDays };
  const showHeidi2CompleteClose = isHeidi2Clinician && allTasksComplete;
  const showNavIndicator =
    !isOfflineGated &&
    totalCount > 0 &&
    !allTasksComplete &&
    !hasStartedActions &&
    completedCount === 0;
  // While the one-time completion celebration is showing, suppress the hover
  // card so two popovers don't overlap on the same anchor.
  const completionTooltipActive =
    !isOfflineGated && allTasksComplete && !hasSeenTrialCompletionTooltip;
  const tierLabel =
    isClinicianTrialUser && !isFakeClinicianTrial
      ? intl.formatMessage(messages.tierNameClinician)
      : isEvidenceTrialUser
        ? intl.formatMessage(messages.tierNameEvidence)
        : intl.formatMessage(messages.tierNamePractice);

  const handleClick = (
    clickSource: 'sidenav_icon' | 'sidenav_expanded' | 'popover_cta'
  ) => {
    if (isOfflineGated) return;

    trackButtonClicked({
      screenName: ScreenName.Sidenav,
      action: ButtonAction.TrialExtensionSidenavButtonClicked,
      label: 'Earn trial days for free',
      clickSource,
    });
    setTrialExtensionModalOpen(true);
  };

  const clearCloseDelay = () => {
    if (closeDelayTimeoutRef.current) {
      clearTimeout(closeDelayTimeoutRef.current);
      closeDelayTimeoutRef.current = null;
    }
  };

  const clearOpenDelay = () => {
    if (openDelayTimeoutRef.current) {
      clearTimeout(openDelayTimeoutRef.current);
      openDelayTimeoutRef.current = null;
    }
  };

  const handleTriggerMouseEnter = () => {
    if (isOfflineGated) return;
    if (completionTooltipActive) return;
    clearCloseDelay();
    openDelayTimeoutRef.current = setTimeout(() => {
      openDelayTimeoutRef.current = null;
      hoverOpenedRef.current = true;
      setPopoverOpen(true);
    }, POPOVER_HOVER_OPEN_DELAY_MS);
  };

  const handleTriggerMouseLeave = () => {
    clearOpenDelay();
    closeDelayTimeoutRef.current = setTimeout(() => {
      closeDelayTimeoutRef.current = null;
      setPopoverOpen(false);
    }, POPOVER_HOVER_CLOSE_DELAY_MS);
  };

  const handleContentMouseEnter = () => clearCloseDelay();
  const handleContentMouseLeave = () => setPopoverOpen(false);

  const handleTriggerClick = () => {
    if (isOfflineGated) return;
    clearOpenDelay();
    clearCloseDelay();
    clickOpenedModalRef.current = true;
    setPopoverOpen(false);
    handleClick('sidenav_icon');
  };

  const rowLabel = allTasksComplete
    ? intl.formatMessage(messages.navLabelEarnedComplete, { days: trialDays })
    : intl.formatMessage(messages.hoverCardTitle, { days: trialDays });

  if (isRibbonSidebarEnabled) {
    const iconClassName = isOfflineGated
      ? 'size-4 shrink-0 text-text-disabled transition-colors'
      : allTasksComplete
        ? 'size-4 shrink-0 text-validation-success'
        : 'size-4 shrink-0 text-tier-pro-text group-hover:text-tier-pro-hover transition-colors';

    // Mobile sheet is a labelled list. The ribbon tile is icon-only and floats.
    if (isMobile) {
      return (
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton
              aria-label={rowLabel}
              disabled={isOfflineGated}
              title={isOfflineGated ? offlineTooltip : undefined}
              data-testid="trial-countdown-button"
              onClick={() => {
                handleTriggerClick();
                dismissMobileSidebar();
              }}
            >
              <Gift className={iconClassName} aria-hidden />
              <span>{rowLabel}</span>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      );
    }

    return (
      <TrialCompletionTooltip allTasksComplete={allTasksComplete} trialDays={trialDays}>
        <SidebarMenu className="items-center">
          <SidebarMenuItem>
            <Popover
              open={!isOfflineGated && popoverOpen && !completionTooltipActive}
              onOpenChange={(open) => {
                if (isOfflineGated) {
                  setPopoverOpen(false);
                  return;
                }
                if (open && clickOpenedModalRef.current) {
                  clickOpenedModalRef.current = false;
                  return;
                }
                setPopoverOpen(open);
                if (open) {
                  trackModalViewed({
                    screenName: ScreenName.TrialExtensionHoverCard,
                  });
                }
              }}
            >
              <PopoverTrigger asChild>
                <SidebarMenuButton
                  layout="iconOnly"
                  className="overflow-hidden"
                  aria-label={intl.formatMessage(messages.earnFreeTrialDays, {
                    days: trialDays,
                  })}
                  // Overrides PopoverTrigger's aria-expanded: the hover card is a
                  // preview, and SidebarMenuButton paints aria-expanded as pressed.
                  aria-expanded={undefined}
                  aria-haspopup="dialog"
                  disabled={isOfflineGated}
                  title={isOfflineGated ? offlineTooltip : undefined}
                  data-testid="trial-countdown-button"
                  onClick={handleTriggerClick}
                  onMouseEnter={handleTriggerMouseEnter}
                  onMouseLeave={handleTriggerMouseLeave}
                >
                  <Gift />
                  {showNavIndicator && (
                    <div
                      className="absolute end-1 top-1.5 z-20 size-1.5 rounded-full bg-accent-blue"
                      data-testid="trial-extension-nav-indicator"
                      aria-hidden="true"
                    />
                  )}
                  {totalCount > 0 && completedCount > 0 && (
                    <Progress
                      value={progressPercent}
                      className="absolute inset-x-0 bottom-0 h-[6px] rounded-none"
                      aria-label={intl.formatMessage(messages.progressLabel, {
                        completed: completedCount,
                        total: totalCount,
                      })}
                    />
                  )}
                </SidebarMenuButton>
              </PopoverTrigger>
              {!isOfflineGated && (
                <PopoverContent
                  side="right"
                  sideOffset={12}
                  align="end"
                  className="w-62 max-w-62"
                  aria-labelledby="trial-extension-popover-title"
                  aria-describedby="trial-extension-popover-desc"
                  onMouseEnter={handleContentMouseEnter}
                  onMouseLeave={handleContentMouseLeave}
                  onOpenAutoFocus={(e) => {
                    if (hoverOpenedRef.current) {
                      e.preventDefault();
                      hoverOpenedRef.current = false;
                    }
                  }}
                >
                  <div className="flex flex-col gap-3">
                    {/* Header: icon + title */}
                    <div className="flex items-center gap-2">
                      <Gift className="size-5 shrink-0 text-text-primary" aria-hidden />
                      <TypographyP2SemiBold
                        id="trial-extension-popover-title"
                        className="text-text-primary"
                      >
                        {allTasksComplete
                          ? intl.formatMessage(messages.hoverCardTitleEarnedComplete, {
                              days: trialDays,
                            })
                          : intl.formatMessage(messages.hoverCardTitle, {
                              days: trialDays,
                            })}
                      </TypographyP2SemiBold>
                    </div>
                    <TypographyCaption
                      id="trial-extension-popover-desc"
                      className="text-text-secondary"
                    >
                      {getHoverCardSubtitle(
                        progress,
                        t,
                        intl,
                        tierLabel,
                        hoverCardCopyOptions
                      )}
                    </TypographyCaption>
                    {!isHeidi2Clinician && totalCount > 0 && completedCount > 0 && (
                      <Progress
                        value={progressPercent}
                        className="h-2 w-full rounded-full"
                        aria-label={intl.formatMessage(messages.progressLabel, {
                          completed: completedCount,
                          total: totalCount,
                        })}
                      />
                    )}
                    <div className={cn(showHeidi2CompleteClose && 'flex gap-2')}>
                      {showHeidi2CompleteClose ? (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="flex-1"
                          onClick={() => {
                            setPopoverOpen(false);
                            setEarnDaysNavPermanentlyDismissed(true);
                          }}
                          data-testid="trial-countdown-hover-card-close"
                        >
                          {intl.formatMessage(
                            trialExtensionProgressMessages.hoverCardCtaCloseHeidi2Clinician
                          )}
                        </Button>
                      ) : null}
                      <Button
                        variant={isHeidi2Clinician ? 'default' : 'pro'}
                        size="sm"
                        className={showHeidi2CompleteClose ? 'flex-1' : 'w-full'}
                        onClick={() => {
                          setPopoverOpen(false);
                          handleClick('popover_cta');
                        }}
                        data-testid="trial-countdown-hover-card-cta"
                      >
                        {getHoverCardCta(progress, t, intl, hoverCardCopyOptions)}
                      </Button>
                    </div>
                  </div>
                </PopoverContent>
              )}
            </Popover>
          </SidebarMenuItem>
        </SidebarMenu>
      </TrialCompletionTooltip>
    );
  }

  // Previous (old) sidebar: nav item with progress states and matching design for collapsed/expanded
  const isCollapsed = sidebarState === 'collapsed';

  const progressCountLabel = totalCount > 0 ? `${completedCount}/${totalCount}` : null;

  const collapsedTooltipHeadline = isOfflineGated
    ? offlineTooltip
    : allTasksComplete
      ? intl.formatMessage(messages.navLabelEarnedComplete, { days: trialDays })
      : intl.formatMessage(messages.hoverCardTitle, { days: trialDays });
  const collapsedTooltipDescription = isOfflineGated
    ? null
    : allTasksComplete
      ? intl.formatMessage(messages.collapsedTooltipAllComplete)
      : totalCount > 0
        ? intl.formatMessage(messages.collapsedTooltipProgress, {
            completed: completedCount,
            total: totalCount,
          })
        : null;

  const isComplete = allTasksComplete;
  const bgClass = isOfflineGated
    ? 'bg-background-secondary-disabled hover:bg-background-secondary-disabled'
    : isComplete
      ? 'bg-validation-success/15 hover:bg-validation-success/25'
      : 'bg-tier-pro-muted hover:bg-tier-pro/20';
  /** Icon box transparent so no separate darker circle; parent button provides the purple bg and hover. */
  const iconBoxBgClass = 'bg-transparent group-hover:bg-transparent';
  const iconTextClass = isOfflineGated
    ? 'text-text-disabled group-hover:text-text-disabled'
    : isComplete
      ? 'text-validation-success group-hover:text-validation-success-hover'
      : 'text-tier-pro-hover group-hover:text-tier-pro-hover';

  const content = (
    <button
      type="button"
      onClick={() => handleClick('sidenav_expanded')}
      aria-label={intl.formatMessage(messages.earnFreeTrialDays, { days: trialDays })}
      disabled={isOfflineGated}
      title={isOfflineGated ? offlineTooltip : undefined}
      data-testid="trial-countdown-button"
      className={cn(
        'group flex w-full min-w-0 flex-col overflow-hidden rounded-md transition-colors',
        bgClass,
        isCollapsed && 'items-center'
      )}
    >
      {/* Row 1: p-2 + size-4 icon + gap-2 to match SidebarMenuButton so icon and label align with Help/Notifications */}
      <div
        className={cn(
          'flex h-8 w-full min-w-0 items-center gap-2 p-2',
          isCollapsed && 'justify-center'
        )}
      >
        {/* Icon box: size-4 when expanded (matches other nav icons), size-8 when collapsed */}
        <div
          className={cn(
            'relative flex shrink-0 items-center justify-center overflow-hidden rounded-md transition-colors',
            isCollapsed ? 'size-8' : 'size-4',
            iconBoxBgClass
          )}
        >
          <Gift className={cn('size-4', iconTextClass)} />
        </div>
        {!isCollapsed && (
          <>
            <span
              className={cn(
                'min-w-0 flex-1 truncate text-left text-xs font-medium leading-snug',
                iconTextClass
              )}
            >
              {rowLabel}
            </span>
            {progressCountLabel !== null && completedCount > 0 && (
              <span
                className={cn(
                  'shrink-0 text-xs font-normal leading-snug tracking-normal',
                  isOfflineGated
                    ? 'text-text-disabled'
                    : isComplete
                      ? 'text-validation-success'
                      : 'text-text-secondary'
                )}
              >
                {progressCountLabel}
              </span>
            )}
          </>
        )}
      </div>
      {totalCount > 0 && completedCount > 0 && (
        <Progress
          value={progressPercent}
          className={cn(
            'h-1.5 w-full shrink-0 rounded-none',
            isOfflineGated && 'opacity-40 grayscale'
          )}
        />
      )}
    </button>
  );

  return (
    <TrialCompletionTooltip
      allTasksComplete={allTasksComplete}
      trialDays={trialDays}
      anchorClassName="w-full min-w-0"
    >
      <SidebarMenuItem className="w-full min-w-0">
        {isCollapsed ? (
          <Tooltip>
            <TooltipTrigger asChild>{content}</TooltipTrigger>
            <TooltipContent
              side="right"
              className="flex flex-col items-center gap-0.5 px-2 py-1.5 text-center text-sm"
            >
              <TypographyCaption className="text-sm font-normal text-text-primary-invert">
                {collapsedTooltipHeadline}
              </TypographyCaption>
              {collapsedTooltipDescription != null && (
                <TypographyFootNote className="text-xs text-text-primary-invert/80">
                  {collapsedTooltipDescription}
                </TypographyFootNote>
              )}
            </TooltipContent>
          </Tooltip>
        ) : (
          content
        )}
      </SidebarMenuItem>
    </TrialCompletionTooltip>
  );
}
